// src/Component/SideData/Overview/AiAgentWorkFlow/utils/uiFieldComponentsShared.jsx
//
// ============================================================
// SHARED UI FIELD COMPONENTS LIBRARY HELPERS
// ============================================================
//
// This module holds the pieces of the UI Field Components
// Library feature that were previously inlined in MyQNATest.jsx:
//
//   • resolveFieldComponent      — priority-ordered resolver
//   • fetchUiFieldComponentsLibrary — network helper
//   • ComponentPickerModal       — import-from-library picker
//   • PinConfirmModal            — pin/unpin confirmation
//
// Two admin screens use it:
//   • MyQNATest.jsx  (LeadGen form editor)
//   • IntentIndex.js (AI Agent Skills editor)
//
// Rationale for extraction: pin semantics, snapshot shape, and
// library-fetch auth are the contract between the two screens
// and the client SDK. Duplicating them invites drift — one
// screen would eventually start writing a snapshot shape that
// the other's resolver can't read, and nothing would catch it
// at compile time.
// ============================================================

import React, { useState } from "react";
import { MdOutlineClose, MdOutlineWidgets } from "react-icons/md";
import faqStyle from "../../../../app_components/FaqStyle.module.css";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

// ============================================================
// RESOLVER
// ============================================================
//
// Given a field node and the current library, return the
// effective fieldType / options / min / max / step that should
// drive the UI.
//
// Priority (matches the backend-side resolver and the client-
// side fieldComponentResolver in client_core):
//   1. Pinned with a snapshot → use the snapshot.
//   2. component_id + library hit → use the library's values.
//   3. Otherwise → use the field's own stored values (legacy
//      shape, or a field that has never been attached).
//
// Note that a *half-populated* pinned state (pinned=true but
// pinned_snapshot missing) falls through to the library. This
// is deliberate: a pinned flag with no snapshot is a bug on the
// write side, and silently trusting the flag alone would hide
// it. Falling through to the library gives the user a working
// field and lets the write bug surface elsewhere.
export function resolveFieldComponent(field, library) {
  if (
    field.pinned &&
    field.pinned_snapshot &&
    typeof field.pinned_snapshot === "object"
  ) {
    return {
      source: "pinned",
      fieldType: field.pinned_snapshot.fieldType || field.fieldType || "text",
      options: field.pinned_snapshot.options || [],
      min: field.pinned_snapshot.min ?? "",
      max: field.pinned_snapshot.max ?? "",
      step: field.pinned_snapshot.step ?? "",
    };
  }

  if (field.component_id && Array.isArray(library)) {
    const comp = library.find((c) => c.component_id === field.component_id);
    if (comp) {
      return {
        source: "library",
        fieldType: comp.type || field.fieldType || "text",
        options: Array.isArray(comp.options) ? comp.options : [],
        min: comp.min ?? "",
        max: comp.max ?? "",
        step: comp.step ?? "",
      };
    }
  }

  return {
    source: "field",
    fieldType: field.fieldType || "text",
    options: field.options || [],
    min: field.min ?? "",
    max: field.max ?? "",
    step: field.step ?? "",
  };
}

// ============================================================
// FETCH HELPER
// ============================================================
//
// Fetch the flat list of components for (appName, region) plus
// the current library version.
//
// Reuses the same /ui_field_component endpoint that the Library
// admin screen uses. That endpoint accepts either Authorization
// (admin path) or x-api-key (public/client path); we send the
// Authorization header because every caller here is admin-side.
//
// Returns { components: [...], version: <int> }. On failure,
// returns { components: [], version: 0 } — never throws.
export async function fetchUiFieldComponentsLibrary({
  appName,
  region,
  baseUrl = backend_api_base_url,
} = {}) {
  if (!appName) {
    return { components: [], version: 0 };
  }

  const regionLower = (region || "us").toLowerCase();
  const apiBase = `https://${regionLower}.autobot2.${baseUrl}`;

  const token = localStorage.getItem("token") || "";

  try {
    const resp = await fetch(
      `${apiBase}/ui_field_component?app_name=${encodeURIComponent(appName)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      },
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`HTTP ${resp.status}${text ? " – " + text : ""}`);
    }

    const data = await resp.json();
    return {
      components: Array.isArray(data?.components) ? data.components : [],
      version: Number.isFinite(data?.version) ? Number(data.version) : 0,
    };
  } catch (err) {
    console.warn(
      "[uiFieldComponentsShared] library fetch failed:",
      err.message || err,
    );
    return { components: [], version: 0 };
  }
}

// ============================================================
// COMPONENT PICKER MODAL
// ============================================================
//
// Opens when the admin clicks either "Import from Library" on a
// new field or "Import from Library" on an existing field's
// config. The caller decides what "select" means:
//
//   • In MyQNATest.jsx (LeadGen), selection can either create a
//     new field from the component (pickerMode = "create-new")
//     or attach to an existing field (pickerMode = "attach-to-
//     existing"). Both paths are handled by the caller's
//     onSelect callback.
//
//   • In IntentIndex.js (AI Agent Skills), selection always
//     attaches to a specific derived field, so the caller's
//     onSelect just mutates that field's component_id + type +
//     options and clears any pin state.
//
// The modal itself is agnostic — it just renders the list,
// filters by name, and calls onSelect(component).
export function ComponentPickerModal({
  isOpen,
  onClose,
  library,
  onSelect,
  loading,
}) {
  const [filter, setFilter] = useState("");

  if (!isOpen) return null;

  const filtered = (library || []).filter((c) =>
    (c.name || "").toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className={faqStyle.modalOverlay}>
      <div
        className={faqStyle.modal}
        style={{
          maxWidth: 620,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <h3 className={faqStyle.modalTitle} style={{ margin: 0 }}>
            Import UI Field Component
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: "#6b7280",
            }}
          >
            <MdOutlineClose />
          </button>
        </div>

        <div style={{ padding: "12px 20px" }}>
          <input
            type="text"
            placeholder="Filter by name..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={faqStyle.textInput}
            style={{ width: "100%" }}
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 20px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {loading && <p>Loading library...</p>}

          {!loading && (!library || library.length === 0) && (
            <p style={{ color: "#6b7280", fontSize: 13 }}>
              No components in the library yet. Create some in the
              <strong> UI Field Components Library </strong>
              section first.
            </p>
          )}

          {!loading &&
            filtered.map((c) => (
              <button
                key={c.component_id}
                onClick={() => onSelect(c)}
                style={{
                  textAlign: "left",
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: "10px 14px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <strong style={{ fontSize: 14 }}>{c.name}</strong>
                  <span style={{ fontSize: 11, color: "#4a7feb" }}>
                    {c.type}
                  </span>
                </div>
                {c.default_placeholder && (
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Placeholder: {c.default_placeholder}
                  </span>
                )}
                {Array.isArray(c.options) && c.options.length > 0 && (
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    {c.options.length} option
                    {c.options.length > 1 ? "s" : ""}
                  </span>
                )}
              </button>
            ))}

          {!loading &&
            filtered.length === 0 &&
            library &&
            library.length > 0 && (
              <p style={{ color: "#6b7280", fontSize: 13 }}>
                No components match "{filter}".
              </p>
            )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PIN CONFIRMATION MODAL
// ============================================================
//
// Explains the trade-off so the admin makes an informed choice:
// pinning freezes the field's definition at the current library
// values; unpinning resumes live reference.
//
// Both screens use the same copy and behavior so the mental
// model stays consistent.
export function PinConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  mode,
  fieldLabel,
  componentName,
}) {
  if (!isOpen) return null;

  const isPinning = mode === "pin";

  return (
    <div className={faqStyle.modalOverlay}>
      <div className={faqStyle.modal} style={{ maxWidth: 480 }}>
        <h3 className={faqStyle.modalTitle}>
          {isPinning ? "Pin this field?" : "Unpin this field?"}
        </h3>

        <div className={faqStyle.modalContent}>
          {isPinning ? (
            <>
              <p style={{ marginTop: 0, lineHeight: 1.6 }}>
                The field <strong>"{fieldLabel}"</strong> will be frozen at the
                current values from{" "}
                <strong>{componentName || "the library component"}</strong>.
              </p>
              <p style={{ lineHeight: 1.6 }}>
                Future edits to the library component will{" "}
                <strong>not</strong> affect this field. The field keeps the
                type, options, and numeric bounds it has right now.
              </p>
              <p style={{ lineHeight: 1.6, color: "#6b7280", fontSize: 13 }}>
                Useful when you want this form (or skill) to stay stable even
                if the shared component evolves.
              </p>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0, lineHeight: 1.6 }}>
                The field <strong>"{fieldLabel}"</strong> will resume live
                reference to{" "}
                <strong>{componentName || "the library component"}</strong>.
              </p>
              <p style={{ lineHeight: 1.6 }}>
                It will pick up the component's current definition, and any
                future library edits will flow into this field.
              </p>
              <p style={{ lineHeight: 1.6, color: "#6b7280", fontSize: 13 }}>
                The field's snapshot is discarded.
              </p>
            </>
          )}
        </div>

        <div className={faqStyle.modalActions}>
          <button className={faqStyle.modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button className={faqStyle.modalConfirm} onClick={onConfirm}>
            {isPinning ? "Pin field" : "Unpin field"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// LIBRARY ATTACHMENT STATUS STRIP
// ============================================================
//
// A small inline component that renders the current attachment
// state for a field: which component it's attached to (if any),
// whether it's pinned, and the three buttons that operate on
// the attachment (Re-import, Pin/Unpin, Detach).
//
// Both screens render this in the same place — next to the
// field's Type dropdown — so the admin sees one consistent
// "Library:" row on every field that could be attached.
//
// The component is presentational. It calls back to the parent
// for every action (open picker, re-import, pin toggle, detach).
export function LibraryAttachmentStrip({
  field,
  attachedComponent,
  pinnedSummary,
  isConfigured,
  readOnly,
  onOpenPicker,
  onReimport,
  onDetach,
  onRequestPinToggle,
}) {
  const disabled = isConfigured || readOnly;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: "#4a5568",
          fontWeight: 500,
          letterSpacing: "0.02em",
        }}
      >
        Library:
      </span>

      {field.component_id ? (
        <>
          <span
            style={{
              fontSize: 12,
              background: "#eef3ff",
              color: "#4a7feb",
              padding: "3px 10px",
              borderRadius: 10,
              fontWeight: 500,
            }}
            title={`component_id: ${field.component_id}`}
          >
            📦 {attachedComponent?.name || "(missing component)"}
          </span>

          <button
            type="button"
            onClick={onReimport}
            disabled={disabled}
            className={faqStyle.actionBtn}
            title="Pull the latest definition from the library"
            style={{
              fontSize: 11,
              padding: "3px 10px",
              opacity: disabled ? 0.55 : 1,
            }}
          >
            Re-import
          </button>

          <button
            type="button"
            onClick={onRequestPinToggle}
            disabled={disabled}
            className={faqStyle.actionBtn}
            title={
              field.pinned
                ? "This field is pinned — click to resume live reference"
                : "Pin this field to freeze the current library values"
            }
            style={{
              fontSize: 11,
              padding: "3px 10px",
              opacity: disabled ? 0.55 : 1,
              ...(field.pinned
                ? {
                    background: "#fef3c7",
                    border: "1px solid #f59e0b",
                    color: "#92400e",
                  }
                : {}),
            }}
          >
            {field.pinned ? "🔒 Pinned" : "🔓 Pin"}
          </button>

          <button
            type="button"
            onClick={onDetach}
            disabled={disabled}
            className={faqStyle.actionBtn}
            title="Remove the library attachment. The field keeps its current values but stops following the component."
            style={{
              fontSize: 11,
              padding: "3px 10px",
              opacity: disabled ? 0.55 : 1,
              color: "#9b2c2c",
            }}
          >
            Detach
          </button>

          {pinnedSummary}
        </>
      ) : (
        <button
          type="button"
          onClick={onOpenPicker}
          disabled={disabled}
          className={faqStyle.actionBtn}
          style={{
            fontSize: 11,
            padding: "3px 10px",
            opacity: disabled ? 0.55 : 1,
            background: "linear-gradient(135deg, #eef3ff 0%, #e0e9ff 100%)",
            border: "1px solid #c7d7fb",
            color: "#3a6fd4",
          }}
          title="Attach a reusable UI Field Component to this field"
        >
          <MdOutlineWidgets style={{ marginRight: 4 }} size={12} />
          Import from Library
        </button>
      )}
    </div>
  );
}

// ============================================================
// PINNED SNAPSHOT SUMMARY
// ============================================================
//
// Small human-readable summary of what a pinned field has been
// frozen to. Rendered below the attachment strip when
// field.pinned is true.
export function PinnedSnapshotSummary({ snapshot }) {
  if (!snapshot) return null;

  const optionCount = Array.isArray(snapshot.options)
    ? snapshot.options.length
    : 0;

  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 11,
        color: "#92400e",
        background: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: 6,
        padding: "6px 10px",
        lineHeight: 1.5,
        width: "100%",
      }}
    >
      <strong>Frozen:</strong> type <code>{snapshot.fieldType}</code>
      {optionCount > 0 && (
        <>
          {" · "}
          {optionCount} option{optionCount > 1 ? "s" : ""}
        </>
      )}
      {snapshot.min !== "" && snapshot.min !== undefined && (
        <>
          {" · "}min {String(snapshot.min)}
        </>
      )}
      {snapshot.max !== "" && snapshot.max !== undefined && (
        <>
          {" · "}max {String(snapshot.max)}
        </>
      )}
    </div>
  );
}

// ============================================================
// DEFAULT EXPORT
// ============================================================
//
// Named exports are the primary API. The default export exists
// so a caller that wants the whole bundle can grab it in one
// import.
export default {
  resolveFieldComponent,
  fetchUiFieldComponentsLibrary,
  ComponentPickerModal,
  PinConfirmModal,
  LibraryAttachmentStrip,
  PinnedSnapshotSummary,
};